import { WIZARD_STEP_CARD_POINTER_HEIGHT, WIZARD_STEP_CARD_POINTER_TARGET_GAP } from './constants';
import {
  CORNER_POINTER_LAYOUT,
  getCornerPointerTargetGapPx,
  getCornerPointerVariant,
  RIGHT_TOP_LEFT_SIDE_SPAN_PX,
  RIGHT_TOP_LEFT_TIP_FROM_CARD_TOP_PX,
} from './cornerPointer';
import type { SpotlightShape, WizardPlacement, WizardPointerAnchor } from './types';

const VIEWPORT_MARGIN = 16;

export type PointerAlignRect = {
  centerX: number;
  centerY: number;
  bottom: number;
};

export type StepCardLayoutInput = {
  placement: WizardPlacement;
  pointerAnchor?: WizardPointerAnchor;
  spotlight: SpotlightShape;
  cardWidth: number;
  cardHeight: number;
  align?: PointerAlignRect;
};

export type StepCardLayoutResult = {
  top: number;
  left: number;
};

function clampToViewport(
  top: number,
  left: number,
  cardWidth: number,
  cardHeight: number,
): { top: number; left: number } {
  const maxLeft = window.innerWidth - cardWidth - VIEWPORT_MARGIN;
  const maxTop = window.innerHeight - cardHeight - VIEWPORT_MARGIN;

  return {
    left: Math.min(Math.max(VIEWPORT_MARGIN, left), maxLeft),
    top: Math.min(Math.max(VIEWPORT_MARGIN, top), maxTop),
  };
}

export function getStepCardLayout(input: StepCardLayoutInput): StepCardLayoutResult {
  const {
    placement,
    pointerAnchor,
    spotlight,
    cardWidth,
    cardHeight,
    align,
  } = input;

  const gap = WIZARD_STEP_CARD_POINTER_TARGET_GAP;
  const spotlightCenterX = spotlight.left + spotlight.width / 2;
  const spotlightCenterY = spotlight.top + spotlight.height / 2;
  const cornerVariant = getCornerPointerVariant(placement, pointerAnchor);
  const targetGap = cornerVariant
    ? getCornerPointerTargetGapPx(cornerVariant)
    : WIZARD_STEP_CARD_POINTER_HEIGHT;

  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top': {
      if (
        cornerVariant === 'top-bottom-right' &&
        align &&
        pointerAnchor === 'bottom-right'
      ) {
        const { tipInsetFromCorner } = CORNER_POINTER_LAYOUT['top-bottom-right'];
        top = align.centerY - cardHeight - targetGap - gap;
        left = align.centerX - cardWidth + tipInsetFromCorner;
      } else {
        top = spotlight.top - cardHeight - targetGap - gap;
        if (pointerAnchor === 'bottom-right' && align) {
          left =
            align.centerX -
            cardWidth +
            CORNER_POINTER_LAYOUT['top-bottom-right'].tipInsetFromCorner;
        } else if (pointerAnchor === 'bottom-right') {
          left = spotlight.left + spotlight.width - cardWidth;
        } else {
          left = spotlightCenterX - cardWidth / 2;
        }
      }
      break;
    }
    case 'bottom': {
      if (
        cornerVariant === 'bottom-top-right' &&
        align &&
        pointerAnchor === 'top-right'
      ) {
        const { tipInsetFromCorner } = CORNER_POINTER_LAYOUT['bottom-top-right'];
        top = align.bottom + gap + targetGap;
        left = align.centerX - cardWidth + tipInsetFromCorner;
      } else {
        top = spotlight.top + spotlight.height + targetGap + gap;
        left =
          pointerAnchor === 'top-right'
            ? spotlight.left + spotlight.width - cardWidth
            : spotlightCenterX - cardWidth / 2;
      }
      break;
    }
    case 'left':
      top = (align?.centerY ?? spotlightCenterY) - cardHeight / 2;
      left =
        spotlight.left -
        cardWidth -
        (cornerVariant === 'right-top-left'
          ? RIGHT_TOP_LEFT_SIDE_SPAN_PX
          : WIZARD_STEP_CARD_POINTER_HEIGHT) -
        gap;
      break;
    case 'right':
      top =
        pointerAnchor === 'top-left' && cornerVariant === 'right-top-left'
          ? (align?.centerY ?? spotlightCenterY) -
            RIGHT_TOP_LEFT_TIP_FROM_CARD_TOP_PX
          : (align?.centerY ?? spotlightCenterY) - cardHeight / 2;
      left =
        spotlight.left +
        spotlight.width +
        (cornerVariant === 'right-top-left'
          ? RIGHT_TOP_LEFT_SIDE_SPAN_PX
          : WIZARD_STEP_CARD_POINTER_HEIGHT) +
        gap;
      break;
  }

  const clamped = clampToViewport(top, left, cardWidth, cardHeight);

  return {
    top: Math.round(clamped.top),
    left: Math.round(clamped.left),
  };
}
